/**
 * Client API pour Liquid dashboard.
 *
 * Backend : merchant-api (PayDRC) via le router additif `/liquid/*`
 * (voir `app/routers/liquid_router.py`).
 *
 * Auth : JWT Bearer dans Authorization header. Access en mémoire, refresh en
 * sessionStorage (survit au reload, pas à la fermeture d'onglet).
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "https://liquid.gofreshpay.com";

let _accessToken: string | null = null;
let _refreshToken: string | null = null;

export function setTokens(access: string, refresh: string): void {
  _accessToken = access;
  _refreshToken = refresh;
  sessionStorage.setItem("liquid_refresh", refresh);
}

export function clearTokens(): void {
  _accessToken = null;
  _refreshToken = null;
  sessionStorage.removeItem("liquid_refresh");
}

export function getAccessToken(): string | null {
  return _accessToken;
}

export function getRefreshToken(): string | null {
  if (_refreshToken) return _refreshToken;
  const stored = sessionStorage.getItem("liquid_refresh");
  if (stored) {
    _refreshToken = stored;
    return stored;
  }
  return null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public type: string,
    public title: string,
    public detail: string,
    public requestId?: string,
    public extra?: Record<string, unknown>,
  ) {
    super(detail || title);
    this.name = "ApiError";
  }
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  skipAuth?: boolean;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined | null>;
};

function buildQueryString(query?: Record<string, unknown>): string {
  if (!query) return "";
  const parts: string[] = [];
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, skipAuth = false, headers = {}, query } = options;

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };

  if (body !== undefined) {
    finalHeaders["Content-Type"] = "application/json";
  }

  if (!skipAuth && _accessToken) {
    finalHeaders.Authorization = `Bearer ${_accessToken}`;
  }

  const fullPath = `${path}${buildQueryString(query)}`;
  const url = fullPath.startsWith("http") ? fullPath : `${API_BASE}${fullPath}`;
  const resp = await fetch(url, {
    method,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "omit",
  });

  if (!resp.ok) {
    let problem: Record<string, unknown>;
    try {
      problem = (await resp.json()) as Record<string, unknown>;
    } catch {
      problem = { detail: await resp.text().catch(() => "") };
    }
    // FastAPI renvoie souvent {detail: "..."} — on l'expose comme title+detail
    const detail = (problem.detail as string) ?? "";
    throw new ApiError(
      resp.status,
      (problem.type as string) ?? "",
      (problem.title as string) ?? detail ?? `HTTP ${resp.status}`,
      detail,
      problem.request_id as string | undefined,
      problem,
    );
  }

  if (resp.status === 204) {
    return undefined as T;
  }

  return (await resp.json()) as T;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export type LoginResponse = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  must_change_password?: boolean;
};

export type Role = "it" | "commercial" | "finance" | "merchant_admin" | "admin";

export type MeResponse = {
  id: string;
  email: string;
  role: Role;
  merchant_id: string | null;
  first_name: string | null;
  last_name: string | null;
  must_change_password?: boolean;
};

export async function login(email: string, password: string): Promise<LoginResponse> {
  const resp = await apiRequest<LoginResponse>("/liquid/auth/login", {
    method: "POST",
    body: { email, password },
    skipAuth: true,
  });
  setTokens(resp.access_token, resp.refresh_token);
  return resp;
}

export async function refreshAccessToken(): Promise<LoginResponse | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const resp = await apiRequest<LoginResponse>("/liquid/auth/refresh", {
      method: "POST",
      body: { refresh_token: refresh },
      skipAuth: true,
    });
    setTokens(resp.access_token, resp.refresh_token);
    return resp;
  } catch {
    clearTokens();
    return null;
  }
}

export async function fetchMe(): Promise<MeResponse> {
  return apiRequest<MeResponse>("/liquid/auth/me");
}

export async function changePassword(current_password: string, new_password: string): Promise<void> {
  await apiRequest("/liquid/auth/change-password", {
    method: "POST",
    body: { current_password, new_password },
  });
}

export function logout(): void {
  clearTokens();
}

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------
export type Payment = {
  id: string;
  merchant_id: string;
  livemode: boolean;
  amount_minor: number;
  // Net crédité au wallet marchand (après commissions Moko/telco)
  amount_merchant_minor?: number;
  commission_minor?: number;
  currency: string;
  payment_method: string;
  status: string;
  external_reference: string;
  // Références explicites (Liquid demande les 3 dans la liste des paiements)
  paydrc_reference: string | null;
  thirdparty_reference: string | null;
  telco_reference: string | null;
  switch_reference?: string | null;
  parsed_metadata: Record<string, unknown> | null;
  customer_phone: string | null;
  customer_email: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  failure_code: string | null;
  failure_message: string | null;
  created_at: string;
  completed_at: string | null;
  merchant_business_name?: string | null;
  merchant_code?: string | null;
};

export type PaymentList = { data: Payment[]; next_cursor: string | null; has_more: boolean };

export async function listPayments(params: {
  cursor?: string;
  limit?: number;
  status?: string;
  payment_method?: string;
  merchant_id?: string;
  /** Recherche large : client, thirdparty_reference, paydrc_reference, telco_reference, switch_reference. */
  q?: string;
}): Promise<PaymentList> {
  return apiRequest<PaymentList>("/liquid/payments", { query: params });
}

/** URL absolue vers le CSV export — à ouvrir dans un nouveau tab avec Authorization header. */
export function paymentsExportUrl(params: {
  status?: string;
  payment_method?: string;
  q?: string;
  date_from?: string;
  date_to?: string;
}): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.append(k, v);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return `${API_BASE}/liquid/payments/export${suffix}`;
}

/**
 * Déclenche le download du CSV — fetch avec Bearer token puis blob URL.
 * (On ne peut pas juste `window.open(url)` car il faut le header Authorization.)
 */
export async function downloadPaymentsExport(params: {
  status?: string;
  payment_method?: string;
  q?: string;
  date_from?: string;
  date_to?: string;
}): Promise<void> {
  const url = paymentsExportUrl(params);
  const token = getAccessToken();
  const resp = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new ApiError(resp.status, "", `Export impossible (HTTP ${resp.status})`, "");
  const blob = await resp.blob();
  const dl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = dl;
  const cd = resp.headers.get("Content-Disposition") || "";
  const match = /filename="?([^";]+)"?/.exec(cd);
  a.download = match ? match[1] : `liquid_payments_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(dl);
}

export async function getPaymentAdmin(id: string): Promise<Payment & Record<string, unknown>> {
  return apiRequest(`/liquid/payments/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// Merchant overview
// ---------------------------------------------------------------------------
export type CurrencyVolume = { currency: string; amount_minor: number };

export type MerchantOverview = {
  period_days: number;
  today: { count: number; volumes: CurrencyVolume[] };
  totals: {
    count: number;
    succeeded_count: number;
    succeeded_volumes: CurrencyVolume[];
    conversion_rate: number;
  };
  by_status: Record<string, { count: number; volumes: CurrencyVolume[] }>;
  by_method: Record<string, { count: number; volumes: CurrencyVolume[] }>;
  wallets: Array<{
    type: "mobile_money" | "cards";
    currency: string;
    balance_minor: number;
    reserved_balance_minor: number;
    // Détail par opérateur (mpesa, orange, airtel, afrimoney) — pour drill-down
    by_operator?: Array<{
      method: string;
      balance_minor: number;
      reserved_balance_minor: number;
    }>;
  }>;
};

export async function getMerchantOverview(days = 30): Promise<MerchantOverview> {
  return apiRequest(`/liquid/overview`, { query: { days } });
}

// ---------------------------------------------------------------------------
// Merchant settings
// ---------------------------------------------------------------------------
export type MerchantSettings = {
  merchant_id: string;
  business_name: string;
  business_email: string;
  business_phone: string | null;
  country: string;
  livemode: boolean;
  logo_data_url: string | null;
  callback_url: string | null;
  commission_rate_mobile_bps: number;
  commission_rate_cards_bps: number;
  refund_window_days: number;
};

export async function getMerchantSettings(): Promise<MerchantSettings> {
  return apiRequest(`/liquid/settings`);
}

export async function updateMerchantSettings(input: {
  business_phone?: string | null;
  callback_url?: string | null;
}): Promise<{ status: string }> {
  return apiRequest(`/liquid/settings`, { method: "PATCH", body: input });
}

// ---------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------
export type TeamMember = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: "merchant_admin" | "finance" | "it" | "commercial";
  status: "active" | "suspended";
  last_login_at: string | null;
  created_at: string;
};

export async function listTeam(): Promise<{ data: TeamMember[] }> {
  return apiRequest(`/liquid/team`);
}

export type InviteTeamInput = {
  email: string;
  first_name?: string;
  last_name?: string;
  role: "merchant_admin" | "finance" | "it" | "commercial";
};

export type TeamInviteResponse = {
  user_id: string;
  email: string;
  temp_password: string;
  login_url: string;
  email_sent?: boolean;
  warning: string;
};

export async function inviteTeamMember(input: InviteTeamInput): Promise<TeamInviteResponse> {
  return apiRequest(`/liquid/team/invite`, { method: "POST", body: input });
}

export async function updateTeamRole(userId: string, role: TeamMember["role"]): Promise<{ status: string }> {
  return apiRequest(`/liquid/team/${userId}/role`, { method: "PATCH", body: { role } });
}

export async function revokeTeamMember(userId: string): Promise<{ status: string }> {
  return apiRequest(`/liquid/team/${userId}/revoke`, { method: "POST" });
}

export async function activateTeamMember(userId: string): Promise<{ status: string }> {
  return apiRequest(`/liquid/team/${userId}/activate`, { method: "POST" });
}

export async function editTeamMember(userId: string, input: { first_name?: string; last_name?: string }): Promise<{ status: string }> {
  return apiRequest(`/liquid/team/${userId}`, { method: "PATCH", body: input });
}

export async function deleteTeamMember(userId: string): Promise<{ status: string }> {
  return apiRequest(`/liquid/team/${userId}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// API keys
// ---------------------------------------------------------------------------
export type ApiKeyInfo = {
  id: string;
  api_key: string;
  api_secret_last4: string;
  livemode: boolean;
  status: "active" | "rotating" | "revoked";
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

export async function listMerchantApiKeys(): Promise<{ data: ApiKeyInfo[] }> {
  return apiRequest(`/liquid/api-keys`);
}

export type RotatedApiKey = {
  api_key: string;
  api_secret: string;
  hmac_callback_secret: string;
  warning: string;
};

export async function rotateMerchantApiKey(): Promise<RotatedApiKey> {
  return apiRequest(`/liquid/api-keys/rotate`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Movements
// ---------------------------------------------------------------------------
export type WalletMovementFull = {
  id: string;
  wallet_id: string;
  merchant_id: string;
  type: string;
  amount_minor: number;
  // null si pas d'audit pour cette transaction (rows historiques pré-fix)
  balance_after_minor: number | null;
  currency: string;
  reference_type: string;
  reference_id: string;
  description: string | null;
  created_at: string;
  wallet_type: "mobile_money" | "cards";
  wallet_currency: string;
  // Commissions (dépôts PayDRC)
  commission_minor?: number;
  commission_freshpay_minor?: number;
  commission_telco_minor?: number;
  amount_merchant_minor?: number;
  merchant_business_name?: string | null;
  merchant_code?: string | null;
};

export async function listMovements(params: {
  wallet_id?: string;
  currency?: string;
  movement_type?: string;
  date_from?: string;
  date_to?: string;
  cursor?: string;
  limit?: number;
  merchant_id_filter?: string;
} = {}): Promise<{ data: WalletMovementFull[]; next_cursor: string | null; has_more: boolean }> {
  return apiRequest(`/liquid/movements`, { query: params });
}

// ---------------------------------------------------------------------------
// Funnel
// ---------------------------------------------------------------------------
export type FunnelStats = {
  period_days: number;
  by_status: Record<string, { count: number; volumes: CurrencyVolume[] }>;
};

export async function getFunnelStats(params: { merchant_id?: string; days?: number } = {}): Promise<FunnelStats> {
  return apiRequest("/liquid/funnel", { query: params });
}

// ---------------------------------------------------------------------------
// Support tickets
// ---------------------------------------------------------------------------
export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";
export type TicketPriority = "low" | "medium" | "high" | "urgent";

export type TicketAttachment = {
  name: string;
  type: string;
  size: number;
  data_url: string;
};

export type TicketMessage = {
  id: number;
  ticket_id: string;
  sender_type: "user" | "staff";
  sender_name: string | null;
  message: string;
  attachments: TicketAttachment[] | null;
  created_at: string;
};

export type TicketSummary = {
  id: number;
  ticket_id: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: string;
  created_by: string;
  created_at: string;
  updated_at: string | null;
  message_count: number;
  unread_by_merchant: boolean;
  last_message_at: string | null;
};

export type TicketDetail = TicketSummary & {
  description: string;
  email: string | null;
  messages: TicketMessage[];
};

export async function listTickets(params: {
  status?: TicketStatus | "all";
  priority?: TicketPriority | "all";
  page?: number;
  per_page?: number;
} = {}): Promise<{
  data: TicketSummary[];
  pagination: { page: number; per_page: number; total: number; total_pages: number };
}> {
  return apiRequest("/liquid/tickets", { query: params });
}

export async function getTicket(ticketId: string, opts: { mark_viewed?: boolean } = {}): Promise<TicketDetail> {
  return apiRequest(`/liquid/tickets/${encodeURIComponent(ticketId)}`, {
    query: { mark_viewed: opts.mark_viewed },
  });
}

export async function createTicket(input: {
  subject: string;
  description: string;
  priority?: TicketPriority;
  category?: string;
  attachments?: TicketAttachment[];
}): Promise<TicketDetail> {
  return apiRequest("/liquid/tickets", { method: "POST", body: input });
}

export async function addTicketMessage(
  ticketId: string,
  input: { message: string; attachments?: TicketAttachment[] },
): Promise<TicketMessage> {
  return apiRequest(`/liquid/tickets/${encodeURIComponent(ticketId)}/messages`, {
    method: "POST",
    body: input,
  });
}

// mark-viewed est un side-effect de GET /liquid/tickets/{id}.
// Cette fonction est un no-op côté client mais gardée pour compatibilité de signature.
export async function markTicketViewed(_ticketId: string): Promise<void> {
  // Le backend marque automatiquement le ticket vu lors du GET.
  return;
}
